import { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import type { SopCobertura, SopResumoMes, StatusCobertura } from '@/types/dashboards';
import { descreverCobertura } from '@/utils/equiplexSop';
import { margemDoRotuloEmPe, type MedidasRotulo } from '@/utils/rotulosGrafico';
import { fmtBRL, fmtBRLCompacto, fmtPreco, mesCurto } from '@/utils/painelFormat';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_GRAFICO, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import PainelCard from '../PainelCard';
import { rotuloNoTopo } from '../rotuloBarra';
import TooltipBox from '../TooltipBox';

interface Ponto {
  mes: string;
  rotulo: string;
  titulo: string;
  valor: number;
  pmv: number | null;
  cobertura: SopCobertura | undefined;
}

const ROTULO_COBERTURA: Record<StatusCobertura, string> = {
  ok: 'Cobertura ok',
  baixo: 'Abaixo do histórico',
  // `sem_dados` = a réplica ainda não chegou no mês (fração decorrida 0), não "zero notas".
  sem_dados: 'Réplica ainda não chegou no mês',
  sem_base: 'Sem histórico p/ comparar',
};

const rodapeCobertura = (c: SopCobertura): string =>
  c.status === 'ok' || c.status === 'baixo' ? `${ROTULO_COBERTURA[c.status]}: ${descreverCobertura(c)}` : ROTULO_COBERTURA[c.status];

interface Props {
  resumos: SopResumoMes[];
  cobertura: SopCobertura[];
  cores: CoresPainel;
}

/** Uma coluna por mês com "R$ 11,9 mi": deitado, ~74 px por mês; em pé, ~22 (03/11/2026). */
const MEDIDAS: MedidasRotulo = { reservado: 92, deitado: 74, emPe: 22 };

/**
 * Faturamento fiscal realizado por mês de produção. Não existe "faturamento
 * planejado" no banco — por decisão, o painel mostra só o real, com um selo de
 * cobertura que compara o mês com a mediana dos seis anteriores (proporcional aos
 * dias decorridos). A cobertura fica no tooltip: o mês abaixo do histórico já tem
 * aviso no topo e ícone no resumo mensal, e a lista embaixo do gráfico repetia isso.
 */
const FaturamentoChart: FC<Props> = ({ resumos, cobertura, cores }) => {
  const porMes = new Map(cobertura.map((c) => [c.mes, c]));
  const dados: Ponto[] = resumos
    .filter((r) => !r.futuro)
    .map((r) => ({
      mes: r.mes,
      rotulo: mesCurto(r.label),
      titulo: r.label,
      valor: r.faturado_valor,
      pmv: r.pmv_realizado,
      cobertura: porMes.get(r.mes),
    }));
  const topoEmPe = margemDoRotuloEmPe(dados.map((d) => fmtBRLCompacto(d.valor)), FONTE_ROTULO);

  const conteudoTooltip = ({ active, payload }: TooltipContentProps) => {
    const p = active && payload?.[0] ? (payload[0].payload as Ponto) : null;
    if (!p) return null;
    return (
      <TooltipBox
        titulo={p.titulo}
        linhas={[
          { rotulo: 'Faturado', valor: fmtBRL(p.valor), cor: cores.faturado },
          { rotulo: 'Preço médio', valor: fmtPreco(p.pmv) },
        ]}
        rodape={p.cobertura ? rodapeCobertura(p.cobertura) : undefined}
      />
    );
  };

  return (
    <PainelCard
      testId="sop-chart-faturamento"
      titulo="Faturamento realizado"
      subtitulo="Notas de saída que contam como venda, bruto, sem devoluções. Entrega futura conta na remessa, como na DRE."
    >
      <AreaDoGrafico altura={ALTURA_GRAFICO} categorias={dados.length} medidas={MEDIDAS}>
        {(modo) => (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dados} margin={{ top: modo === 'em-pe' ? Math.max(topoEmPe, 24) : 24, right: 8, bottom: 0, left: 0 }} barCategoryGap="36%">
              <CartesianGrid vertical={false} stroke={cores.grade} />
              <XAxis dataKey="rotulo" tick={tickEixo(cores)} axisLine={{ stroke: cores.eixo }} tickLine={false} />
              <YAxis
                tickFormatter={(v: number) => fmtBRLCompacto(v)}
                tick={tickEixo(cores)}
                axisLine={false}
                tickLine={false}
                width={84}
              />
              <Tooltip content={conteudoTooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
              <Bar dataKey="valor" name="Faturado" fill={cores.faturado} radius={[4, 4, 0, 0]} maxBarSize={56} isAnimationActive={false}>
                {modo && <LabelList dataKey="valor" content={rotuloNoTopo(fmtBRLCompacto, cores.texto, undefined, modo === 'em-pe')} />}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </AreaDoGrafico>
    </PainelCard>
  );
};

export default FaturamentoChart;
